import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute, PublicOnlyRoute } from './ProtectedRoute';
import { ROUTES } from './routes';
import { moduleDashboardPath } from './modules';
import { PERMISSIONS } from '@/types/permissions';

import { LoginPage } from '@/features/auth/LoginPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { HomePage } from '@/features/os/HomePage';
import { ModuleDashboardPage } from '@/features/os/ModuleDashboardPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { SchoolsPage } from '@/features/schools/SchoolsPage';
import { CampusesPage } from '@/features/campuses/CampusesPage';
import { AcademicYearsPage } from '@/features/academic-years/AcademicYearsPage';
import { GradesPage } from '@/features/grades/GradesPage';
import { GradeOfferingsPage } from '@/features/grade-offerings/GradeOfferingsPage';
import { SectionsPage } from '@/features/sections/SectionsPage';
import { SubjectsPage } from '@/features/subjects/SubjectsPage';
import { StudentsPage } from '@/features/students/StudentsPage';
import { StudentDetailPage } from '@/features/students/StudentDetailPage';
import { EnrollmentsPage } from '@/features/students/EnrollmentsPage';
import { GuardiansPage } from '@/features/guardians/GuardiansPage';
import { GuardianDetailPage } from '@/features/guardians/GuardianDetailPage';
import { PromotionPage } from '@/features/promotion/PromotionPage';
import { EmployeesPage } from '@/features/staff/EmployeesPage';
import { TeachersPage } from '@/features/staff/TeachersPage';
import { CurriculumPage } from '@/features/teaching/CurriculumPage';
import { TeacherAssignmentsPage } from '@/features/teaching/TeacherAssignmentsPage';
import { PeriodsPage } from '@/features/teaching/PeriodsPage';
import { TimetablePage } from '@/features/teaching/TimetablePage';
import { AssignmentsPage } from '@/features/teaching/AssignmentsPage';
import { ExaminationsPage } from '@/features/examinations/ExaminationsPage';
import { ExaminationDetailPage } from '@/features/examinations/ExaminationDetailPage';
import { MarksEntryPage } from '@/features/examinations/MarksEntryPage';
import { ResultsPage } from '@/features/examinations/ResultsPage';
import { ReportCardPage } from '@/features/examinations/ReportCardPage';
import { HrDashboardPage } from '@/features/hr/HrDashboardPage';
import { HrReportsPage } from '@/features/hr/HrReportsPage';
import { HrSettingsPage } from '@/features/hr/HrSettingsPage';
import { LeaveRequestsPage } from '@/features/hr/LeaveRequestsPage';
import { LeaveTypesPage } from '@/features/hr/LeaveTypesPage';
import { LeaveBalancesPage } from '@/features/hr/LeaveBalancesPage';
import { MyLeavePage } from '@/features/hr/MyLeavePage';
import { MyPayslipsPage } from '@/features/hr/MyPayslipsPage';
import { LoansPage } from '@/features/hr/LoansPage';
import { PayrollPage } from '@/features/hr/PayrollPage';
import { PayrollDetailPage } from '@/features/hr/PayrollDetailPage';
import { PayslipPage } from '@/features/hr/PayslipPage';
import { SalaryAssignmentsPage } from '@/features/hr/SalaryAssignmentsPage';
import { SalaryComponentsPage } from '@/features/hr/SalaryComponentsPage';
import { SalaryStructuresPage } from '@/features/hr/SalaryStructuresPage';
import { AcquisitionDetailPage } from '@/features/library/AcquisitionDetailPage';
import { AcquisitionsPage } from '@/features/library/AcquisitionsPage';
import { BookDetailPage } from '@/features/library/BookDetailPage';
import { CataloguePage } from '@/features/library/CataloguePage';
import { CategoriesPage } from '@/features/library/CategoriesPage';
import { CirculationDeskPage } from '@/features/library/CirculationDeskPage';
import { InventoryAuditPage } from '@/features/library/InventoryAuditPage';
import { InventoryPage } from '@/features/library/InventoryPage';
import { LibraryDashboardPage } from '@/features/library/LibraryDashboardPage';
import { LibraryFinesPage } from '@/features/library/LibraryFinesPage';
import { LibraryMemberDetailPage } from '@/features/library/LibraryMemberDetailPage';
import { LibraryMembersPage } from '@/features/library/LibraryMembersPage';
import { LibraryReportsPage } from '@/features/library/LibraryReportsPage';
import { LibrarySettingsPage } from '@/features/library/LibrarySettingsPage';
import { LoansPage as LibraryLoansPage } from '@/features/library/LoansPage';
import { LostAndDamagedPage } from '@/features/library/LostAndDamagedPage';
import { MyLibraryPage } from '@/features/library/MyLibraryPage';
import { OverdueLoansPage } from '@/features/library/OverdueLoansPage';
import { ReservationsPage } from '@/features/library/ReservationsPage';
import { SuppliersPage } from '@/features/library/SuppliersPage';
import { TransportDashboardPage } from '@/features/transport/TransportDashboardPage';
import { DailyOperationsPage } from '@/features/transport/DailyOperationsPage';
import { VehiclesPage } from '@/features/transport/VehiclesPage';
import { VehicleDetailPage } from '@/features/transport/VehicleDetailPage';
import { DriversPage } from '@/features/transport/DriversPage';
import { DriverDetailPage } from '@/features/transport/DriverDetailPage';
import { RoutesPage } from '@/features/transport/RoutesPage';
import { RouteDetailPage } from '@/features/transport/RouteDetailPage';
import { EnrollmentsPage as TransportEnrollmentsPage } from '@/features/transport/EnrollmentsPage';
import { TripsPage } from '@/features/transport/TripsPage';
import { TripDetailPage } from '@/features/transport/TripDetailPage';
import { IncidentsPage } from '@/features/transport/IncidentsPage';
import { MaintenancePage } from '@/features/transport/MaintenancePage';
import { TransportReportsPage } from '@/features/transport/TransportReportsPage';
import { TransportSettingsPage } from '@/features/transport/TransportSettingsPage';
import { MyTransportPage } from '@/features/transport/MyTransportPage';
import { AssetsPage } from '@/features/assets/AssetsPage';
import { AssetDetailPage } from '@/features/assets/AssetDetailPage';
import { AssetAuditsPage } from '@/features/assets/AssetAuditsPage';
import { AssetAuditDetailPage } from '@/features/assets/AssetAuditDetailPage';
import { StockPage } from '@/features/assets/StockPage';
import { AssetSetupPage } from '@/features/assets/AssetSetupPage';
import { AdmissionsPage } from '@/features/admissions/AdmissionsPage';
import { AdmissionDetailPage } from '@/features/admissions/AdmissionDetailPage';
import { AnnouncementsPage } from '@/features/announcements/AnnouncementsPage';
import { MyAnnouncementsPage } from '@/features/announcements/MyAnnouncementsPage';
import { AuditTrailPage } from '@/features/audit/AuditTrailPage';
import { ImportPage } from '@/features/imports/ImportPage';
import { FinanceDashboardPage } from '@/features/finance/FinanceDashboardPage';
import { InvoicesPage } from '@/features/finance/InvoicesPage';
import { InvoiceDetailPage } from '@/features/finance/InvoiceDetailPage';
import { PaymentsPage } from '@/features/finance/PaymentsPage';
import { PaymentDetailPage } from '@/features/finance/PaymentDetailPage';
import { ReceiptPage } from '@/features/finance/ReceiptPage';
import { FeeStructuresPage } from '@/features/finance/FeeStructuresPage';
import { FeeAssignmentsPage } from '@/features/finance/FeeAssignmentsPage';
import { DiscountsPage } from '@/features/finance/DiscountsPage';
import { ScholarshipsPage } from '@/features/finance/ScholarshipsPage';
import { FinanceSetupPage } from '@/features/finance/FinanceSetupPage';
import { RefundsPage } from '@/features/finance/RefundsPage';
import { FinanceReportsPage } from '@/features/finance/FinanceReportsPage';
import { AttendancePage } from '@/features/attendance/AttendancePage';
import { StaffAttendancePage } from '@/features/attendance/StaffAttendancePage';
import { CalendarPage } from '@/features/calendar/CalendarPage';
import { UsersPage } from '@/features/users/UsersPage';
import { RolesPage } from '@/features/roles/RolesPage';
import { PermissionsPage } from '@/features/permissions/PermissionsPage';
import { ForbiddenPage, NotFoundPage } from '@/features/errors/ErrorPages';
import { ChildrenHomePage } from '@/features/portal/ChildrenHomePage';
import { OverviewPage } from '@/features/portal/OverviewPage';
import { PortalAttendancePage } from '@/features/portal/PortalAttendancePage';
import { PortalTimetablePage } from '@/features/portal/PortalTimetablePage';
import { PortalResultsPage } from '@/features/portal/PortalResultsPage';
import { PortalResultDetailPage } from '@/features/portal/PortalResultDetailPage';
import { PortalFeesPage } from '@/features/portal/PortalFeesPage';
import { PortalReceiptPage } from '@/features/portal/PortalReceiptPage';
import { PortalBusPage } from '@/features/portal/PortalBusPage';
import { PortalLibraryPage } from '@/features/portal/PortalLibraryPage';
import { PortalAssignmentsPage } from '@/features/portal/PortalAssignmentsPage';
import { MyTeachingPage } from '@/features/teaching/MyTeachingPage';

/**
 * The route table.
 *
 * Each protected route declares the permission it needs, so a user who reaches
 * a URL directly is redirected rather than shown a screen full of 403s. This is
 * still only presentation — every request the page makes is authorised
 * independently by the backend.
 */
export function AppRouter() {
  return (
    <Routes>
      <Route element={<PublicOnlyRoute />}>
        <Route path={ROUTES.login} element={<LoginPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          {/*
            School OS: the launcher, then one dashboard per module, then the
            module's pages below. Module dashboards are navigation only; each
            card leads to a route guarded on its own below.
          */}
          <Route path={ROUTES.dashboard} element={<HomePage />} />
          <Route path="/dashboard" element={<Navigate to={ROUTES.dashboard} replace />} />
          <Route path={moduleDashboardPath()} element={<ModuleDashboardPage />} />
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.DASHBOARD_VIEW]} />}>
            <Route path={ROUTES.insights} element={<DashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.NOTIFICATION_SELF_VIEW]} />}>
            <Route path={ROUTES.notifications} element={<NotificationsPage />} />
          </Route>
          <Route path={ROUTES.forbidden} element={<ForbiddenPage />} />

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.STUDENT_VIEW]} />}>
            <Route path={ROUTES.students} element={<StudentsPage />} />
            <Route path={ROUTES.studentDetail()} element={<StudentDetailPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ADMISSION_VIEW]} />}>
            <Route path={ROUTES.admissions} element={<AdmissionsPage />} />
            <Route path={ROUTES.admissionDetail()} element={<AdmissionDetailPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ENROLLMENT_VIEW]} />}>
            <Route path={ROUTES.enrollments} element={<EnrollmentsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ENROLLMENT_PROMOTE]} />}>
            <Route path={ROUTES.promotion} element={<PromotionPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.GUARDIAN_VIEW]} />}>
            <Route path={ROUTES.guardians} element={<GuardiansPage />} />
            <Route path={ROUTES.guardianDetail()} element={<GuardianDetailPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ACADEMIC_YEAR_VIEW]} />}>
            <Route path={ROUTES.academicYears} element={<AcademicYearsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.GRADE_VIEW]} />}>
            <Route path={ROUTES.grades} element={<GradesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.GRADE_OFFERING_VIEW]} />}>
            <Route path={ROUTES.gradeOfferings} element={<GradeOfferingsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.SECTION_VIEW]} />}>
            <Route path={ROUTES.sections} element={<SectionsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.SUBJECT_VIEW]} />}>
            <Route path={ROUTES.subjects} element={<SubjectsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.EMPLOYEE_VIEW]} />}>
            <Route path={ROUTES.employees} element={<EmployeesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TEACHER_VIEW]} />}>
            <Route path={ROUTES.teachers} element={<TeachersPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.EMPLOYEE_ATTENDANCE_VIEW]} />}>
            <Route path={ROUTES.staffAttendance} element={<StaffAttendancePage />} />
          </Route>

          {/*
            HR. The dashboard is guarded by EMPLOYEE_VIEW rather than a
            dedicated permission: it is the HR landing page, and its payroll
            figures are omitted server-side for anybody who cannot see payroll.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.EMPLOYEE_VIEW]} />}>
            <Route path={ROUTES.hrDashboard} element={<HrDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LEAVE_REQUEST_VIEW]} />}>
            <Route path={ROUTES.leaveRequests} element={<LeaveRequestsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LEAVE_TYPE_MANAGE]} />}>
            <Route path={ROUTES.leaveTypes} element={<LeaveTypesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LEAVE_BALANCE_VIEW]} />}>
            <Route path={ROUTES.leaveBalances} element={<LeaveBalancesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.SALARY_COMPONENT_MANAGE]} />}>
            <Route path={ROUTES.salaryComponents} element={<SalaryComponentsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.SALARY_STRUCTURE_VIEW]} />}>
            <Route path={ROUTES.salaryStructures} element={<SalaryStructuresPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.SALARY_ASSIGNMENT_VIEW]} />}>
            <Route path={ROUTES.salaryAssignments} element={<SalaryAssignmentsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.PAYROLL_PERIOD_VIEW]} />}>
            <Route path={ROUTES.payroll} element={<PayrollPage />} />
            <Route path={ROUTES.payrollDetail()} element={<PayrollDetailPage />} />
            <Route path={ROUTES.hrReports} element={<HrReportsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LOAN_VIEW]} />}>
            <Route path={ROUTES.loans} element={<LoansPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.HR_SETTINGS_MANAGE]} />}>
            <Route path={ROUTES.hrSettings} element={<HrSettingsPage />} />
          </Route>

          {/*
            Self-service. A payslip is reachable by anybody who may read one at
            all — the page picks the self-service endpoint for somebody without
            PAYSLIP_VIEW, and that endpoint can only return their own.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LEAVE_REQUEST_CREATE]} />}>
            <Route path={ROUTES.myLeave} element={<MyLeavePage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.PAYSLIP_SELF_VIEW]} />}>
            <Route path={ROUTES.myPayslips} element={<MyPayslipsPage />} />
            <Route path={ROUTES.payslip()} element={<PayslipPage />} />
          </Route>

          {/*
            Library. The dashboard is guarded by LIBRARY_VIEW rather than a
            reporting permission: it is the module's landing page, and the one
            figure on it that is nobody's business — what the school is owed in
            fines — is omitted server-side for a caller who cannot see fines.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_VIEW]} />}>
            <Route path={ROUTES.libraryDashboard} element={<LibraryDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.CIRCULATION_ISSUE]} />}>
            <Route path={ROUTES.circulationDesk} element={<CirculationDeskPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.CATALOGUE_VIEW]} />}>
            <Route path={ROUTES.catalogue} element={<CataloguePage />} />
            <Route path={ROUTES.bookDetail()} element={<BookDetailPage />} />
            <Route path={ROUTES.libraryCategories} element={<CategoriesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_MEMBER_VIEW]} />}>
            <Route path={ROUTES.libraryMembers} element={<LibraryMembersPage />} />
            <Route path={ROUTES.libraryMemberDetail()} element={<LibraryMemberDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.CIRCULATION_VIEW]} />}>
            <Route path={ROUTES.loansList} element={<LibraryLoansPage />} />
            <Route path={ROUTES.overdueLoans} element={<OverdueLoansPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.RESERVATION_VIEW]} />}>
            <Route path={ROUTES.reservations} element={<ReservationsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_FINE_VIEW]} />}>
            <Route path={ROUTES.libraryFines} element={<LibraryFinesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_INVENTORY_VIEW]} />}>
            <Route path={ROUTES.libraryInventory} element={<InventoryPage />} />
            <Route path={ROUTES.inventoryAudit()} element={<InventoryAuditPage />} />
            <Route path={ROUTES.lostAndDamaged} element={<LostAndDamagedPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ACQUISITION_VIEW]} />}>
            <Route path={ROUTES.acquisitions} element={<AcquisitionsPage />} />
            <Route path={ROUTES.acquisitionDetail()} element={<AcquisitionDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_SUPPLIER_VIEW]} />}>
            <Route path={ROUTES.librarySuppliers} element={<SuppliersPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_REPORT_VIEW]} />}>
            <Route path={ROUTES.libraryReports} element={<LibraryReportsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_MANAGE]} />}>
            <Route path={ROUTES.librarySettings} element={<LibrarySettingsPage />} />
          </Route>

          {/*
            What a reader does for themselves. One page rather than four: a
            reader has one question with four parts, and the endpoints behind it
            are scoped to the signed-in account by the server rather than by a
            parameter this page sends.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.LIBRARY_SELF_VIEW]} />}>
            <Route path={ROUTES.browseCatalogue} element={<MyLibraryPage />} />
          </Route>

          {/*
            Transport. The dashboard is guarded by TRANSPORT_VIEW: it is the
            module's landing page, and every figure on it is a count the
            backend already scopes. Everything else carries its own VIEW
            permission, matching the controller it reads from.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_VIEW]} />}>
            <Route path={ROUTES.transportDashboard} element={<TransportDashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_TRIP_VIEW]} />}>
            <Route path={ROUTES.transportOperations} element={<DailyOperationsPage />} />
            <Route path={ROUTES.trips} element={<TripsPage />} />
            <Route path={ROUTES.tripDetail()} element={<TripDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.VEHICLE_VIEW]} />}>
            <Route path={ROUTES.vehicles} element={<VehiclesPage />} />
            <Route path={ROUTES.vehicleDetail()} element={<VehicleDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_DRIVER_VIEW]} />}>
            <Route path={ROUTES.transportDrivers} element={<DriversPage />} />
            <Route path={ROUTES.transportDriverDetail()} element={<DriverDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_ROUTE_VIEW]} />}>
            <Route path={ROUTES.transportRoutes} element={<RoutesPage />} />
            <Route path={ROUTES.transportRouteDetail()} element={<RouteDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_ENROLLMENT_VIEW]} />}>
            <Route path={ROUTES.transportEnrollments} element={<TransportEnrollmentsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_INCIDENT_VIEW]} />}>
            <Route path={ROUTES.transportIncidents} element={<IncidentsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.VEHICLE_MAINTENANCE_VIEW]} />}>
            <Route path={ROUTES.transportMaintenance} element={<MaintenancePage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_REPORT_VIEW]} />}>
            <Route path={ROUTES.transportReports} element={<TransportReportsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_MANAGE]} />}>
            <Route path={ROUTES.transportSettings} element={<TransportSettingsPage />} />
          </Route>

          {/*
            A driver's own day. One page: their trips, each with its roster,
            scoped to the signed-in account by the server rather than by a
            parameter this page sends.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TRANSPORT_SELF_VIEW]} />}>
            <Route path={ROUTES.myTransport} element={<MyTransportPage />} />
          </Route>

          {/*
            The student and parent portal. A student's pages are their own; a
            guardian's pick a child with ?child=, which the server re-checks on
            every request (404 for a child who is not theirs).
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.STUDENT_CHILD_VIEW]} />}>
            <Route path={ROUTES.myChildren} element={<ChildrenHomePage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.STUDENT_SELF_VIEW]} />}>
            <Route path={ROUTES.myOverview} element={<OverviewPage />} />
          </Route>
          <Route
            element={
              <ProtectedRoute
                permissions={[PERMISSIONS.STUDENT_SELF_VIEW, PERMISSIONS.STUDENT_CHILD_VIEW]}
              />
            }
          >
            <Route path={ROUTES.myAttendance} element={<PortalAttendancePage />} />
            <Route path={ROUTES.myTimetable} element={<PortalTimetablePage />} />
            <Route path={ROUTES.myResults} element={<PortalResultsPage />} />
            <Route path={ROUTES.myResult()} element={<PortalResultDetailPage />} />
            <Route path={ROUTES.myFees} element={<PortalFeesPage />} />
            <Route path={ROUTES.myReceipt()} element={<PortalReceiptPage />} />
            <Route path={ROUTES.myBus} element={<PortalBusPage />} />
            <Route path={ROUTES.myLibraryAccount} element={<PortalLibraryPage />} />
            <Route path={ROUTES.myAssignments} element={<PortalAssignmentsPage />} />
          </Route>

          {/* A teacher's own week, resolved from the account by GET /timetables/me. */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TIMETABLE_VIEW]} />}>
            <Route path={ROUTES.myTeaching} element={<MyTeachingPage />} />
          </Route>

          {/*
            Assets. The register, its detail and the counts are all read with
            ASSET_VIEW; stock has its own view permission; setup (categories
            and locations) is for whoever may manage assets.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ASSET_VIEW]} />}>
            <Route path={ROUTES.assets} element={<AssetsPage />} />
            <Route path={ROUTES.assetAudits} element={<AssetAuditsPage />} />
            <Route path={ROUTES.assetAuditDetail()} element={<AssetAuditDetailPage />} />
            <Route path={ROUTES.assetDetail()} element={<AssetDetailPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.STOCK_VIEW]} />}>
            <Route path={ROUTES.stock} element={<StockPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ASSET_MANAGE]} />}>
            <Route path={ROUTES.assetSetup} element={<AssetSetupPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ACADEMIC_SUBJECT_VIEW]} />}>
            <Route path={ROUTES.curriculum} element={<CurriculumPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TEACHER_VIEW]} />}>
            <Route path={ROUTES.teacherAssignments} element={<TeacherAssignmentsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.PERIOD_VIEW]} />}>
            <Route path={ROUTES.periods} element={<PeriodsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.TIMETABLE_VIEW]} />}>
            <Route path={ROUTES.timetables} element={<TimetablePage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.STUDENT_ATTENDANCE_VIEW]} />}>
            <Route path={ROUTES.attendance} element={<AttendancePage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ASSIGNMENT_VIEW]} />}>
            <Route path={ROUTES.assignments} element={<AssignmentsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.EXAMINATION_VIEW]} />}>
            <Route path={ROUTES.examinations} element={<ExaminationsPage />} />
            <Route path={ROUTES.examinationDetail()} element={<ExaminationDetailPage />} />
            {/* Nested: every one of these also loads the examination itself. */}
            <Route element={<ProtectedRoute permissions={[PERMISSIONS.MARKS_VIEW]} />}>
              <Route path={ROUTES.examinationMarks()} element={<MarksEntryPage />} />
            </Route>
            <Route element={<ProtectedRoute permissions={[PERMISSIONS.RESULT_VIEW]} />}>
              <Route path={ROUTES.examinationResults()} element={<ResultsPage />} />
              <Route path={ROUTES.reportCard()} element={<ReportCardPage />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.FINANCE_REPORT_VIEW]} />}>
            <Route path={ROUTES.financeDashboard} element={<FinanceDashboardPage />} />
            <Route path={ROUTES.financeReports} element={<FinanceReportsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.INVOICE_VIEW]} />}>
            <Route path={ROUTES.invoices} element={<InvoicesPage />} />
            <Route path={ROUTES.invoiceDetail()} element={<InvoiceDetailPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.FEE_STRUCTURE_VIEW]} />}>
            <Route path={ROUTES.feeStructures} element={<FeeStructuresPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.FEE_ASSIGNMENT_VIEW]} />}>
            <Route path={ROUTES.feeAssignments} element={<FeeAssignmentsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.REFUND_VIEW]} />}>
            <Route path={ROUTES.refunds} element={<RefundsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.FEE_CATEGORY_VIEW]} />}>
            <Route path={ROUTES.financeSetup} element={<FinanceSetupPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.DISCOUNT_VIEW]} />}>
            <Route path={ROUTES.discounts} element={<DiscountsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.SCHOLARSHIP_VIEW]} />}>
            <Route path={ROUTES.scholarships} element={<ScholarshipsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.PAYMENT_VIEW]} />}>
            <Route path={ROUTES.payments} element={<PaymentsPage />} />
            <Route path={ROUTES.paymentDetail()} element={<PaymentDetailPage />} />
            <Route path={ROUTES.receipt()} element={<ReceiptPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.CALENDAR_VIEW]} />}>
            <Route path={ROUTES.calendar} element={<CalendarPage />} />
          </Route>

          {/*
            Announcements. The desk is for authors and shows drafts; the feed
            is what the signed-in account's audience sees, resolved by the
            server rather than by a parameter this page sends.
          */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ANNOUNCEMENT_MANAGE]} />}>
            <Route path={ROUTES.announcements} element={<AnnouncementsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ANNOUNCEMENT_VIEW]} />}>
            <Route path={ROUTES.myAnnouncements} element={<MyAnnouncementsPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.AUDIT_VIEW]} />}>
            <Route path={ROUTES.auditTrail} element={<AuditTrailPage />} />
          </Route>

          {/* Spreadsheet import; each type is further gated by its own create permission on the server. */}
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.IMPORT_MANAGE]} />}>
            <Route path={ROUTES.dataImport} element={<ImportPage />} />
          </Route>

          <Route element={<ProtectedRoute permissions={[PERMISSIONS.SCHOOL_VIEW]} />}>
            <Route path={ROUTES.schools} element={<SchoolsPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.CAMPUS_VIEW]} />}>
            <Route path={ROUTES.campuses} element={<CampusesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.USER_VIEW]} />}>
            <Route path={ROUTES.users} element={<UsersPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.ROLE_VIEW]} />}>
            <Route path={ROUTES.roles} element={<RolesPage />} />
          </Route>
          <Route element={<ProtectedRoute permissions={[PERMISSIONS.PERMISSION_VIEW]} />}>
            <Route path={ROUTES.permissions} element={<PermissionsPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
